import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreditCard, BarChart, ExternalLink } from "lucide-react";
import AvailablePlansTab from "@/components/pricing/AvailablePlansTab";
import CurrentPlanUsageTab from "@/components/pricing/CurrentPlanUsageTab";
import PricingFAQ from "@/components/pricing/PricingFAQ";
import SettingsContentWrapper from "./SettingsContentWrapper";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { PlanWithFeatures, fetchPlans } from "@/services/planService";

// Type for display compatibility with AvailablePlansTab
type PlanForDisplay = Omit<PlanWithFeatures, "features"> & {
  features: string[]; // Override features to be string array for AvailablePlansTab compatibility
  price: number; // Added price property mapped from monthly_price
};

const SubscriptionSettingsContent = () => {
  const [plans, setPlans] = useState<PlanForDisplay[]>([]);
  const [currentPlan, setCurrentPlan] = useState<PlanWithFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Use AuthContext instead of fetching organization separately
  const { organization } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const { data: plansData, error: plansError } = await fetchPlans();

        if (plansError) throw plansError;

        // Transform plans data to match website format
        const transformedPlans: PlanForDisplay[] = (plansData || []).map(
          (plan) => {
            let features: string[] = [];
            let everythingInPrevious: string | undefined;
            let additionalFeatures: string[] | undefined;

            if (plan.name === "Starter") {
              features = [
                "30+ ready-to-use AI assistants powered by the latest models",
                "Create your own assistants",
                "700 AI outputs/month",
                "Live web access",
                "Share assistants publicly",
                "Customize assistants",
              ];
            } else if (plan.name === "Pro") {
              everythingInPrevious = "Everything in STARTER +";
              additionalFeatures = [
                "2,000 AI outputs/month",
                "AI Phone Agents",
                "450 voice minutes (~4/6 calls/day)",
                "$0.3/min overage",
                "Teams: Invite additional users ($19/u/m)",
                `Max ${plan.features.max_knowledge_items || 15} knowledge sources (files, websites, snippets)`,
                "Connect to your CRM and 5,000 other tools",
                "Priority support",
              ];
            } else if (plan.name === "Business") {
              everythingInPrevious = "Everything in PRO +";
              additionalFeatures = [
                "4,000 AI outputs/month",
                "More AI Phone agents",
                "1,500 voice minutes",
                "$0.21/min overage",
                "Advanced integrations",
                "Custom voice cloning",
                "Dedicated account manager",
              ];
            }

            return {
              ...plan,
              price: plan.monthly_price,
              features,
              everythingInPrevious,
              additionalFeatures,
            };
          },
        );

        setPlans(transformedPlans);

        // Use organization from AuthContext to find current plan
        if (organization?.plan_id) {
          const currentPlanData = plansData?.find(
            (plan) => plan.id === organization.plan_id,
          );

          if (currentPlanData) {
            setCurrentPlan(currentPlanData);
          }
        }
      } catch (error: any) {
        console.error("Error fetching plans:", error);
        toast({
          variant: "destructive",
          title: "Error loading plans",
          description: error.message,
        });
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, [organization?.plan_id, toast]);

  const handleSubscribe = async (planId: string) => {
    if (!organization?.id) {
      toast({
        variant: "destructive",
        title: "Organization not found",
        description: "Please try logging in again.",
      });
      return;
    }

    setProcessing(true);
    try {
      // Use Supabase Edge Function to handle Stripe logic securely
      const { data, error } = await supabase.functions.invoke(
        "create-stripe-checkout",
        {
          body: {
            organizationId: organization.id,
            planId,
          },
        },
      );
      if (error || !data?.url) {
        throw error || new Error("No session URL returned");
      }
      // Redirect to Stripe session URL
      window.location.href = data.url;
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error creating subscription",
        description: error.message,
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleManageBilling = async () => {
    if (!organization?.id) {
      toast({
        variant: "destructive",
        title: "Organization not found",
        description: "Please try logging in again.",
      });
      return;
    }

    setProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-billing-portal",
        {
          body: {
            organizationId: organization.id,
          },
        },
      );

      if (error || !data?.url) {
        throw error || new Error("No portal URL returned");
      }

      // Open billing portal in new tab
      window.open(data.url, "_blank");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error opening billing portal",
        description: error.message,
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <SettingsContentWrapper title="Subscription & Usage">
        <div className="space-y-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </SettingsContentWrapper>
    );
  }

  // Debug: Add fallback if something goes wrong
  if (!plans || plans.length === 0) {
    return (
      <SettingsContentWrapper title="Subscription & Usage">
        <div className="text-center py-12">
          <p className="text-gray-500">No plans available at the moment.</p>
          <p className="text-sm text-gray-400 mt-2">
            {loading ? "Loading..." : "Please refresh the page to try again."}
          </p>
        </div>
      </SettingsContentWrapper>
    );
  }

  return (
    <SettingsContentWrapper
      title="Subscription & Usage"
      description="Manage your subscription plan and view usage details"
    >
      {organization?.subscription_status === "active" &&
        organization?.current_period_end && (
          <Alert className="mb-8">
            <CreditCard className="h-4 w-4" />
            <AlertTitle className="flex items-center justify-between">
              <span>Subscription Active</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleManageBilling}
                disabled={processing}
                className="ml-4"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Manage Billing
              </Button>
            </AlertTitle>
            <AlertDescription>
              Your next billing date is{" "}
              <strong>
                {new Date(organization.current_period_end).toLocaleDateString(
                  "en-US",
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                )}
              </strong>
              . Your subscription will automatically renew unless cancelled.
              <br />
              <span className="text-sm text-muted-foreground mt-2 block">
                Click "Manage Billing" to view invoices, update payment methods,
                or cancel your subscription.
              </span>
            </AlertDescription>
          </Alert>
        )}

      <Tabs defaultValue="plans" className="w-full">
        <div className="mb-8">
          <TabsList>
            <TabsTrigger value="plans" className="flex items-center">
              <CreditCard className="h-4 w-4 mr-2" />
              Available Plans
            </TabsTrigger>
            {(organization?.subscription_status === "active" ||
              organization?.subscription_status === "trialing") &&
              currentPlan && (
                <TabsTrigger value="usage" className="flex items-center">
                  <BarChart className="h-4 w-4 mr-2" />
                  Current Plan & Usage
                </TabsTrigger>
              )}
          </TabsList>
        </div>

        <TabsContent value="plans">
          <AvailablePlansTab
            plans={plans}
            organization={organization}
            processing={processing}
            onSubscribe={handleSubscribe}
          />
        </TabsContent>

        {(organization?.subscription_status === "active" ||
          organization?.subscription_status === "trialing") &&
          currentPlan && (
            <TabsContent value="usage">
              <CurrentPlanUsageTab
                currentPlan={currentPlan}
                organization={organization}
              />
            </TabsContent>
          )}
      </Tabs>

      <div className="mt-12">
        <PricingFAQ />
      </div>
    </SettingsContentWrapper>
  );
};

export default SubscriptionSettingsContent;
